class PartDiagram extends HTMLElement {
  constructor() {
    super();
    this.section_id = this.dataset.sectionId;
    this.diagram_handle = this.dataset.diagramHandle;
    this.parts_count = parseInt(this.dataset.partsCount, 10) || 0;

    this.desktopViewport = '';

    this.elements = {
      partDiagramWrapper: this.querySelector(".part-diagram-wrapper"),
      partsList: this.querySelector(".parts-list"),
      fixedTableContainer: this.querySelector(".fixed-table-container"),
      normalTableContainer: this.querySelector(".normal-table-container")
    }

    this.scrollToBeginningOfDiagramNumber = this.scrollToBeginningOfDiagramNumber.bind(this)

    if (this.elements.normalTableContainer && this.elements.fixedTableContainer) {
      this.elements.normalTableContainer.addEventListener('scroll', this.handleTableScroll.bind(this, this.elements.normalTableContainer, this.elements.fixedTableContainer));

      this.initializeDataRetrieval();
      this.stickyTableHeader();
    }

    if (this.elements.partDiagramWrapper) {
      let resizeObserver = new ResizeObserver((entries) => this.resizeThumbnailScroll());
      resizeObserver.observe(this.elements.partDiagramWrapper);
    }
  }

  async initializeDataRetrieval() {
    let requestCount = Math.ceil(this.parts_count / 20);
    let partsBody = this.querySelector('tbody');

    if (!requestCount || !partsBody) return;

    let requestArray = [];

    for (let request = 0; request < requestCount; request++) {
      requestArray.push({ index: request, parts_offset: request * 20 })
    }

    let parts;

    try {
      const promises = requestArray.map(request => this.getPartDiagramData(this.diagram_handle, request.parts_offset));
      parts = await Promise.all(promises);
    } catch (error) {
      console.warn(`Keeping server-rendered part diagram rows because endpoint retrieval failed - ${error}`);
      return;
    }

    if (!this.isValidPartsResponse(parts, requestCount)) return;

    let partsHtml = parts.map(part => part.html).join("");
    let nextBody = document.createElement('tbody');
    nextBody.innerHTML = partsHtml;

    let rows = Array.from(nextBody.querySelectorAll('tr'));

    if (rows.length !== this.parts_count) {
      console.warn(`Keeping server-rendered part diagram rows because endpoint returned ${rows.length} rows, expected ${this.parts_count}.`);
      return;
    }

    if (this.hasDuplicateRows(rows)) {
      console.warn('Keeping server-rendered part diagram rows because endpoint returned duplicate rows.');
      return;
    }

    partsBody.innerHTML = partsHtml;
  }

  getPartDiagramData = (diagram_handle, parts_offset) => fetch(`/pages/sale?view=part-diagram-endpoint${`&diagram-handle=${encodeURIComponent(diagram_handle)}`}${`&parts-offset=${parts_offset}`}&page=${Date.now()}`)
    .then((r) => {
      if (!r.ok) {
        throw new Error(`Endpoint returned ${r.status} for offset ${parts_offset}`);
      }

      return r.text();
    })
    .then((responseText) => {
      let html = new DOMParser().parseFromString(responseText, 'text/html');
      let items_source = html.querySelector('tbody');
      if (items_source) {
        return {
          html: items_source.innerHTML,
          offset: parts_offset
        }
      }
      throw new Error(`Missing tbody for offset ${parts_offset}`);
    });

  isValidPartsResponse(parts, requestCount) {
    if (!Array.isArray(parts) || parts.length !== requestCount) {
      console.warn(`Keeping server-rendered part diagram rows because endpoint returned ${parts ? parts.length : 0} chunks, expected ${requestCount}.`);
      return false;
    }

    return parts.every((part, index) => {
      if (!part || typeof part.html !== 'string' || part.html.trim() === '') {
        console.warn(`Keeping server-rendered part diagram rows because endpoint chunk ${index + 1} was empty.`);
        return false;
      }

      return true;
    });
  }

  hasDuplicateRows(rows) {
    let rowSignatures = new Set();

    for (const row of rows) {
      let signature = [
        row.dataset.diagramNumber || '',
        ...Array.from(row.cells).map(cell => cell.textContent.replace(/\s+/g, ' ').trim())
      ].join('|');

      if (rowSignatures.has(signature)) {
        return true;
      }

      rowSignatures.add(signature);
    }

    return false;
  }

  scrollToBeginningOfDiagramNumber(e) {
    let diagramNumber = e.currentTarget.parentElement.dataset.diagramNumber;
    let scrollAmount = 0;
    let parts = Array.from(this.elements.partsList.querySelectorAll('tr'));
    let foundPartIndex = parts.findIndex(part => part.dataset.diagramNumber === diagramNumber);

    if (foundPartIndex !== -1) {
      scrollAmount -= this.elements.normalTableContainer.querySelector('thead').getBoundingClientRect().height;
      scrollAmount += parts[foundPartIndex].getBoundingClientRect().top;
      scrollAmount += window.scrollY;
  
      window.scrollTo({
        top: scrollAmount + 1,
        behavior: 'smooth',
      });
    }
  }

  stickyTableHeader() {
    let fixedTableContainer= this.elements.fixedTableContainer;
    let normalTableContainer= this.elements.normalTableContainer;
    let handleScroll = this.handleTableScroll;

    let resizeObserver = new ResizeObserver((entries) => this.resizeStickyTableHeader(normalTableContainer, fixedTableContainer));
    resizeObserver.observe(normalTableContainer);

      let observerNormal = new IntersectionObserver(
        function (entries) {  
          const { isIntersecting, target, boundingClientRect, intersectionRect, intersectionRatio } = entries[0];
            if (!isIntersecting && intersectionRatio === 0) {
              if (boundingClientRect.y < 0) {
                fixedTableContainer.querySelector('table').classList.add('visible');
                handleScroll(normalTableContainer, fixedTableContainer);
              }
    
            } else {
              fixedTableContainer.querySelector('table').classList.remove('visible');
            }
        },
        {
          threshold: [0]        
        }
      );
      observerNormal.observe(normalTableContainer.querySelector('thead'));

      let observerFixed = new IntersectionObserver(
        function (entries) {
  
          const { isIntersecting, target, boundingClientRect, intersectionRect, intersectionRatio } = entries[0];
            if (!isIntersecting && intersectionRatio === 0) {
              fixedTableContainer.querySelector('table').classList.remove('visible');    
            } else {
              if (boundingClientRect.y > 60) return;
              fixedTableContainer.querySelector('table').classList.add('visible');
              handleScroll(normalTableContainer, fixedTableContainer);
            }
        },
        {
          threshold: [0]        
        }
      );
      observerFixed.observe(normalTableContainer);
    

  }

  resizeStickyTableHeader(normalTableContainer, fixedTableContainer) {
    let normalTableContainerDimensions = normalTableContainer.getBoundingClientRect();
    let normalTableDimensions = normalTableContainer.querySelector('table').getBoundingClientRect();
    let normalTableHeaders = Array.from(normalTableContainer.querySelectorAll('th'));
    let fixedTableHeaders = Array.from(fixedTableContainer.querySelectorAll('th'));

    fixedTableContainer.style = `width:${normalTableContainerDimensions.width}px`;
    fixedTableContainer.querySelector('table').style = `width:${normalTableDimensions.width}px`;

    for (const [index, element] of normalTableHeaders.entries()) {
      let dimensions = element.getBoundingClientRect();
      fixedTableHeaders[index].style = `width:${dimensions.width}px`;
    }
  }

  handleTableScroll(normalTableContainer, fixedTableContainer) {
    let scrollLeft = normalTableContainer.scrollLeft;
    fixedTableContainer.scrollTo({ left: scrollLeft })
  }

  resizeThumbnailScroll() {
    let desktopSize = window.matchMedia("(min-width: 1200px)");
    if(desktopSize.matches && this.desktopViewport !== true) {
      this.desktopViewport = true;
      this.elements.partDiagramWrapper.querySelectorAll('[id^="GalleryThumbnails"] [data-diagram-number]').forEach((diagramThumbnail) => {
        diagramThumbnail.querySelector('button').addEventListener('click', this.scrollToBeginningOfDiagramNumber);
      });
    }
    if(!desktopSize.matches && this.desktopViewport === true) {
      this.desktopViewport = false;
      this.elements.partDiagramWrapper.querySelectorAll('[id^="GalleryThumbnails"] [data-diagram-number]').forEach((diagramThumbnail) => {
        diagramThumbnail.querySelector('button').removeEventListener('click', this.scrollToBeginningOfDiagramNumber);
      });
    }
  }
}

let backTopButton = document.getElementById('backTop');

window.onscroll = function () {
  scrollFunction();
};

function scrollFunction() {
  if (document.body.scrollTop > 2000 || document.documentElement.scrollTop > 2000) {
    backTopButton.style.display = 'flex';
  } else {
    backTopButton.style.display = 'none';
  }
}

function backTop() {
  document.body.scrollTop = 0;
  document.documentElement.scrollTop = 0;
}


customElements.define('part-diagram', PartDiagram);
